import { Component, DestroyRef, EventEmitter, inject, OnDestroy, OnInit, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, UntypedFormGroup } from '@angular/forms';
import { RulesDynamicFormService } from '@app/sharedModule/components/rules-dynamic-form/rules-dynamic-form.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { LayoutService } from '@app/storybook/services/layout/layout.service';
import { ToastService } from '@app/storybook/services/toast/toast.service';
import { SmartCartService, StandaloneWidgetItem } from '../../smart-cart.service';
import {
  BLOCK_TYPE_TO_API_TYPE,
  SmartCartBlockType,
  buildWidgetUsageToken,
} from '../../smart-cart-form/smart-cart-block.model';
import {
  OrderProtectionFormShape,
  createOrderProtectionFormGroup,
} from '../../smart-cart-form/widget-editors/order-protection/order-protection.model';
import {
  attachDisplayConditions,
  readDisplayConditionsGroup,
} from '../../smart-cart-form/components/smart-cart-form-display-conditions/display-conditions.model';

const ORDER_PROTECTION_API_TYPE = BLOCK_TYPE_TO_API_TYPE[SmartCartBlockType.ORDER_PROTECTION] ?? 'order_protection';
const DEFAULT_WIDGET_NAME = 'Order Protection';

@Component({
  selector: 'standalone-widget-form',
  templateUrl: './standalone-widget-form.component.html',
  styleUrls: ['./standalone-widget-form.component.scss'],
  standalone: false,
})
export class StandaloneWidgetFormComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);
  private readonly layoutService = inject(LayoutService);
  private readonly rulesDynamicFormService = inject(RulesDynamicFormService);

  // The whole form IS the Order Protection editor's form group; the widget name is derived from its
  // Display title on save (the Figma editor has no separate name field).
  formGroup!: FormGroup<OrderProtectionFormShape>;
  usageToken = buildWidgetUsageToken(SmartCartBlockType.ORDER_PROTECTION);
  saving = false;
  loading = false;

  private widgetId: number | null = null;
  // The editor is rendered only once this is true, so it initializes once with the loaded formGroup +
  // saved usageToken (never with the throwaway pre-load token, which caused a shifting token + 404s).
  ready = false;
  // The live preview lives in the parent (SmartCartComponent). Emit the form once it's ready so the parent
  // reads a stable reference (not this ViewChild getter, which flips mid change-detection → NG0100).
  @Output() formReady = new EventEmitter<FormGroup<OrderProtectionFormShape>>();

  get orderProtectionForm(): FormGroup<OrderProtectionFormShape> {
    return this.formGroup;
  }

  get displayConditionsForm(): UntypedFormGroup | null {
    return readDisplayConditionsGroup(this.formGroup);
  }

  private buildFormGroup(settings?: Record<string, unknown>): FormGroup<OrderProtectionFormShape> {
    return attachDisplayConditions(
      createOrderProtectionFormGroup(settings ?? null),
      settings,
      this.rulesDynamicFormService,
    );
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private smartCartService: SmartCartService,
    private toastService: ToastService,
  ) {}

  ngOnInit(): void {
    // Mirror the in-drawer widget editor: show a "‹ Back  Order Protection" header (Back returns to the list).
    this.layoutService.customHeaderTitle$.set('Order Protection');
    this.layoutService.headerBackAction$.set(() => this.onCancel());

    // Placeholder so getters/save never hit undefined; the editor stays gated until the real one loads.
    this.formGroup = this.buildFormGroup();

    const idParam = this.route.snapshot.params['id'];
    const id = idParam ? Number(idParam) : NaN;
    if (!isNaN(id)) {
      this.widgetId = id;
      this.load(id);
    } else {
      // New widget: nothing to load, render immediately with a fresh token.
      this.ready = true;
      this.formReady.emit(this.formGroup);
    }
  }

  ngOnDestroy(): void {
    this.layoutService.customHeaderTitle$.set('');
    this.layoutService.headerBackAction$.set(null);
  }

  save(): void {
    if (this.saving) return;
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      this.toastService.showToast({ mode: 'danger', message: 'Please fix the highlighted fields before saving' });
      return;
    }
    this.saving = true;

    this.runSave()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.saving = false;
          this.toastService.showToast({ mode: 'success', message: 'Standalone widget saved' });
          this.router.navigate(['/engage/smart-cart/standalone']);
        },
        error: (err) => {
          this.saving = false;
          console.error('Failed to save standalone widget:', err);
          this.toastService.showToast({ mode: 'danger', message: 'Failed to save standalone widget' });
        },
      });
  }

  onCancel(): void {
    this.router.navigate(['/engage/smart-cart/standalone']);
  }

  private runSave(): Observable<StandaloneWidgetItem> {
    const settings = this.formGroup.getRawValue() as Record<string, unknown>;
    const name = (this.formGroup.controls.title.value || '').trim() || DEFAULT_WIDGET_NAME;

    if (this.widgetId === null) {
      return this.smartCartService.createStandaloneWidget({
        name,
        type: ORDER_PROTECTION_API_TYPE,
        usageToken: this.usageToken,
        settings,
      });
    }
    return this.smartCartService.updateStandaloneWidget(this.widgetId, { name, settings });
  }

  private load(id: number): void {
    this.loading = true;
    this.smartCartService
      .getStandaloneWidget(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (widget) => {
          this.loading = false;
          if (widget.usageToken) this.usageToken = widget.usageToken;
          // Rebuild the form from the saved settings so the list-item FormArray is populated too.
          this.formGroup = this.buildFormGroup(widget.settings ?? {});
          // Render the editor now that the real formGroup + saved token are in place (init once, correctly).
          this.ready = true;
          this.formReady.emit(this.formGroup);
        },
        error: (err) => {
          this.loading = false;
          console.error('Failed to load standalone widget:', err);
          this.toastService.showToast({ mode: 'danger', message: 'Failed to load standalone widget' });
          // Don't strand the user on a blank, never-ready editor — go back to the list. Falling back to a
          // fresh form here would risk overwriting the real (unloaded) widget with defaults on save.
          this.router.navigate(['/engage/smart-cart/standalone']);
        },
      });
  }
}
